import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { from, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ListParameters, ListResponse, transformParameters } from 'src/app/api';
import { ApiHelper } from 'src/app/shared/common/ApiHelper';
import { SwalHelper } from 'src/app/shared/common/SwalHelper';
import { ApiResponse } from 'src/app/shared/models/ApiResponse';
import { FileService } from '../../shared/services/file.service';
import { ImportUrlType } from '../components/sections/import-by-entity/import-by-entity.component';
import { ImportDataWithMappings } from '../interface/import-data-with-mappings.interface';
import { ImportTemplate } from '../interface/import-template.interface';
import { ImportFileResponse, ImportFileResponseEmployee, ImportOverview } from './import.model';

@Injectable({
    providedIn: 'root'
})
export class ImportService {
    public constructor(
        private _apiHelper: ApiHelper,
        private _http: HttpClient,
        private _loaderService: NgxUiLoaderService,
        private _swalHelper: SwalHelper,
        private _fileService: FileService,
    ) { }

    public deleteImport(importID: number | string): Observable<'done' | 'error'> {
        return from(
            new Promise<'done' | 'error'>(async (resolve, reject) => {
                const deleteConfirmed = await this._swalHelper.showConfirmDeleteDialog();

                if (deleteConfirmed) {
                    this._http.delete<ApiResponse>(`/api/imports/${importID}`)
                        .subscribe(
                            response => {
                                this._loaderService.stopLoader('master');

                                this._apiHelper.handleSuccessResponse(response);

                                resolve('done');
                            },
                            error => {
                                this._loaderService.stopLoader('master');

                                this._apiHelper.handleErrorResponse(error);
                            }
                        );
                } else {
                    reject('error');
                }
            })
        );
    }

    public deleteImportItem(importItemID: number | string): Observable<'done' | 'error'> {
        return from(
            new Promise<'done' | 'error'>(async (resolve, reject) => {
                const deleteConfirmed = await this._swalHelper.showConfirmDeleteDialog();

                if (deleteConfirmed) {
                    this._http.delete<ApiResponse>(`/api/imports-items/${importItemID}`)
                        .subscribe(
                            response => {
                                this._loaderService.stopLoader('master');

                                this._apiHelper.handleSuccessResponse(response);

                                resolve('done');
                            },
                            error => {
                                this._loaderService.stopLoader('master');

                                this._apiHelper.handleErrorResponse(error);
                            }
                        );
                } else {
                    reject('error');
                }
            })
        );
    }

    public getAllImportTemplates(): Observable<Array<ImportTemplate>> {
        return this._http.get<Array<any>>('/api/imports-templates');
    }

    public getImportByID(importID: number): Observable<ImportOverview> {
        return this._http.get<ImportOverview>(`/api/imports/${importID}`);
    }

    public getImportsList(params: ListParameters<ImportOverview>, filter: any): Observable<ListResponse<ImportOverview>> {
        return this._http.post<ListResponse<ImportOverview>>('/api/imports/list', {...transformParameters(params), filter});
    }

    public importData(importData: ImportDataWithMappings): Observable<ImportOverview> {
        const formData = new FormData();

        formData.append('mappings', JSON.stringify(importData.mappings));
        formData.append('save_template', String(importData.saveAsTemplate));

        if (importData.headerRowsCount) {
            formData.append('header_rows_count', String(importData.headerRowsCount));
        }

        if (importData.name) {
            formData.append('name', importData.name);
        }

        return of(null)
            .pipe(
                switchMap(() => {
                    return this._http.post<ApiResponse & { import: ImportOverview; }>('/api/imports', formData)
                        .pipe(
                            map(response => {
                                this._apiHelper.handleSuccessResponse(response);

                                return response.import;
                            }),
                            catchError(error => {
                                this._apiHelper.handleErrorResponse(error);

                                return of(null);
                            })
                        );
                })
            );
    }

    public importPreview(importData: any): Observable<any> {
        const formData = new FormData();

        formData.append('file', importData.file);

        if (importData.entityType) {
            formData.append('entity', importData.entityType);
        }

        if (importData.entityTargetId?.toString()) {
            formData.append('entity_target_ID', importData.entityTargetId?.toString());
        }

        if (importData.importTemplateId?.toString()) {
            formData.append('import_template_ID', importData.importTemplateId?.toString());
        }

        return of(null)
            .pipe(
                switchMap(() => {
                    return this._http.post('/api/imports/preview', formData)
                        .pipe(
                            catchError(error => {
                                this._apiHelper.handleErrorResponse(error);

                                return of(null);
                            })
                        );
                })
            );
    }

    public importByEntity(file: File, url: ImportUrlType, confirmed = false, additionalFormData = null): Promise<ImportFileResponse | ImportFileResponseEmployee> {
        this._loaderService.startLoader('master');
        const formData = new FormData();
        formData.append('file', file);
        formData.append('confirmed', confirmed ? '1' : '0');

        if (additionalFormData) {
            for (const key in additionalFormData) {
                if (additionalFormData.hasOwnProperty(key)) {
                    for (const subKey in additionalFormData[key]) {
                        if (additionalFormData[key].hasOwnProperty(subKey)) {
                            formData.append(`${key}[${subKey}]`, additionalFormData[key][subKey]);
                        }
                    }
                }
            }
        }

        return new Promise((resolve) => {
            this._http.post(`/api/${url}`, formData).subscribe((response: ApiResponse & (ImportFileResponse | ImportFileResponseEmployee)) => {
                this._apiHelper.handleSuccessResponse(response);
                this._loaderService.stopLoader('master');
                resolve(response);
            }, error => {
                this._apiHelper.handleErrorResponse(error);
                this._loaderService.stopLoader('master');
            });
        });
    }

    public downloadTemplate(entity: string, locale: string): void {
        this._http.get(`/api/imports/template?entity=${entity}&locale=${locale}`, {responseType: 'arraybuffer', observe: 'response'})
            .subscribe((response) => {
                const contentDisposition = response.headers.get('content-disposition');
                let filename = 'example.xlsx';

                if (contentDisposition) {
                    const match = contentDisposition.match(/filename\*?=(?:UTF-8''|)([^;]+)/i);
                    if (match && match[1]) {
                        filename = decodeURIComponent(match[1].replace(/['"]/g, ''));
                    }
                }
                void this._fileService.downloadFileByPlatform(response.body, filename);
            });
    }
}
